import React, { useContext, useState } from "react";
import { useWindowDimensions } from "react-native";
import {
    useSafeAreaFrame,
    SafeAreaProvider,
} from "react-native-safe-area-context";
import { Defs, LinearGradient, Stop, Rect } from "react-native-svg";
import Result from "../../components/Result";
import PrioritiesResult from "../../components/PrioritiesResult";
import Button from "../../components/Button";
import Snippet, { SnippetCtaDestination } from "../../components/Snippet";
import QuizQueue from "../../components/QuizQueue";
import { FurnitureContext } from "../../utils/furniture";
import {
    QuizContext,
    QuizType,
    QuizOrderContext,
    QuizContextInterface,
} from "../../utils/quiz";
import Redo from "../../components/QuizRedo";
import Restart from "../../components/QuizRestart";
import Spinner from "../../components/Spinner";

import {
    Container,
    Scroll,
    Content,
    Hero,
    Snippets,
    Ledge,
    UpNext,
    Cta,
    RedoContainer,
    RestartContainer,
    RedoText,
} from "./styles";

const destinationRouteMap = {
    HOME: "Home",
    MY_RELATIONSHIP: "MyRelationship",
    MY_STRATEGIES: "MyStrategies",
    MY_RESOURCES: "MyResources",
    MY_SETTINGS: "MySettings",
};

const QuizReview = ({ navigation, route }) => {
    const { isReview = false } = route?.params;
    const furniture = useContext(FurnitureContext);
    // @ts-ignore
    const { getQuiz, redo, quizState, headerHeight } =
        useContext<QuizContextInterface | null>(QuizContext);
    const quizOrder = useContext(QuizOrderContext);
    const { width } = useSafeAreaFrame();
    const windowDimensions = useWindowDimensions();
    const windowWidth = Math.floor(windowDimensions.width);
    const quiz = getQuiz();
    const quizId = quiz._id;
    const photoUrl = `https:${quiz.photo.url}?w=${windowWidth}&fm=jpg`;
    const { dateCompleted } = quizState;
    const [isWaiting, setIsWaiting] = useState(false);

    const renderResult = (type: QuizType) => {
        if (quiz.type === QuizType.Priorities) {
            return <PrioritiesResult result={quiz.result} />;
        }
        return (
            <Result
                type={quiz.type}
                questionCount={quiz.questions.length}
                result={quiz.result}
            />
        );
    };

    return (
        <SafeAreaProvider>
            <Container style={{ paddingTop: headerHeight }}>
                <Scroll>
                    <Content>
                        <Hero source={{ uri: photoUrl }} />
                        {isReview && (
                            <RestartContainer>
                                <Restart
                                    onPress={() => {
                                        setIsWaiting(true);
                                        redo().then(() => {
                                            navigation.replace("Quiz", {
                                                quizId,
                                            });
                                            // TODO: is this needed? investigate - isWaiting is defaulted to false on render
                                            setIsWaiting(false);
                                        });
                                    }}
                                    dateCompleted={dateCompleted}>
                                    {isWaiting && <Spinner />}
                                </Restart>
                            </RestartContainer>
                        )}
                        {!isReview && (
                            <RedoContainer>
                                {isWaiting && <Spinner />}
                                {!isWaiting && (
                                    <>
                                        <RedoText>
                                            {furniture.moodRedo}
                                        </RedoText>
                                        <Redo
                                            onPress={() => {
                                                setIsWaiting(true);
                                                redo().then(() => {
                                                    navigation.replace(
                                                        "Quiz",
                                                        { quizId },
                                                        setIsWaiting(false),
                                                    );
                                                    setIsWaiting(false);
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            </RedoContainer>
                        )}

                        {renderResult(quiz.type)}
                        <Snippets>
                            {quiz.result.snippets?.map((snippet, index) => {
                                const snippetImageUrl = snippet.media
                                    ? `https:${snippet.media.url}?w=${windowWidth}&fm=png`
                                    : null;

                                let destination: SnippetCtaDestination | null =
                                    null;

                                if (
                                    snippet.defaultCtaTitle &&
                                    snippet.defaultCtaDestination
                                ) {
                                    const route =
                                        destinationRouteMap[
                                            snippet.defaultCtaDestination
                                        ];

                                    destination = {
                                        title: snippet.defaultCtaTitle,
                                        onPress: () => {
                                            navigation.jumpTo(route, {
                                                _meta: {
                                                    shouldResetRoot: true,
                                                },
                                            });
                                        },
                                    };
                                }

                                return (
                                    <Snippet
                                        key={snippet._id}
                                        type={snippet.type}
                                        title={snippet.title}
                                        titleRich={snippet.titleRich}
                                        body={snippet.body}
                                        bodyRich={snippet.bodyRich}
                                        imageUrl={snippetImageUrl}
                                        resource={snippet.resource}
                                        resourceCtaType={
                                            snippet.resourceCtaType
                                        }
                                        resourceCtaTitle={
                                            snippet.resourceCtaTitle
                                        }
                                        isOdd={index % 2 === 0}
                                        destination={destination}
                                    />
                                );
                            })}
                        </Snippets>
                        {quizOrder.next && quizOrder?.queue?.length > -1 && (
                            <>
                                <Ledge width={`${width}`} height="16">
                                    <Defs>
                                        <LinearGradient
                                            id="ledgeGradient"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1">
                                            <Stop
                                                offset="0"
                                                stopColor="#000000"
                                                stopOpacity={0.2}
                                            />
                                            <Stop
                                                offset="1"
                                                stopColor="#000000"
                                                stopOpacity={0}
                                            />
                                        </LinearGradient>
                                    </Defs>
                                    <Rect
                                        width="100%"
                                        height="100%"
                                        fill="url(#ledgeGradient)"
                                    />
                                </Ledge>
                                <UpNext type="h2">
                                    {furniture.strings.quizResultUpcomingTitle}
                                </UpNext>
                                <QuizQueue
                                    skipId={quiz._id}
                                    queue={[quizOrder.next, ...quizOrder.queue]}
                                    onPressItem={quizId =>
                                        navigation.navigate("Quiz", {
                                            quizId,
                                        })
                                    }
                                />
                            </>
                        )}
                    </Content>
                </Scroll>
                <Cta>
                    <Button
                        title={furniture.strings.goHomeCta}
                        onPress={() => {
                            /**
                             * We're calling rest instead of
                             * navigation.dangerouslyGetParent().goBack();
                             * to force a return to the home screen from the quiz review
                             *
                             * stakeholders found the variability in return screen to be confusing
                             *
                             * So here we're forcing the issue
                             */
                            navigation.reset({
                                index: 0,
                                routes: [{ name: "Home" }],
                            });
                        }}
                    />
                </Cta>
            </Container>
        </SafeAreaProvider>
    );
};

export default QuizReview;
